import React, {
 useEffect, useRef, useImperativeHandle, forwardRef
} from 'react';
import {
 Card, CardContent, CardHeader, CardTitle
} from 'shared/components/ui/card';
import {
 EditorView, lineNumbers, highlightActiveLine, Decoration
} from '@codemirror/view';
import {
 EditorState, StateEffect, StateField
} from '@codemirror/state';
import {
 json
} from '@codemirror/lang-json';
import {
 highlightSelectionMatches
} from '@codemirror/search';
import {
 autocompletion
} from '@codemirror/autocomplete';
import {
    codeFolding,
    foldGutter,
} from '@codemirror/language';

// Define the effect for setting error line
const setErrorLineEffect = StateEffect.define<number | null>();

// Define the state field for error line highlighting
const errorLineField = StateField.define({
    create() {
        return Decoration.none;
    },
    update(deco, tr) {
        deco = deco.map(tr.changes);
        for (const e of tr.effects) {
            if (e.is(setErrorLineEffect)) {
                if (e.value == null) return Decoration.none;

                // CM line numbers are 1-based: doc.line(n)
                const line = tr.state.doc.line(e.value);
                return Decoration.set([
                    Decoration.line({
                        class: 'cm-errorLine'
                    }).range(line.from),
                ]);
            }
        }
        return deco;
    },
    provide: (f) => EditorView.decorations.from(f),
});

// Theme for error line highlighting
const errorLineTheme = EditorView.theme({
    '.cm-errorLine': {
        backgroundColor: 'rgba(244, 63, 94, 0.12)', // subtle red highlight
    },
});

export type EditorPanelHandle = {
    highlightErrorAt: (lineNumber: number) => void;
    clearErrorHighlight: () => void;
};

type EditorPanelProps = {
    editorValue: string;
    isValid: boolean;
    validationMessage: string;
    flowIndex: string;
    onChange: (value: string) => void;
};

const EditorPanel = forwardRef<EditorPanelHandle, EditorPanelProps>(({
    editorValue,
    isValid,
    validationMessage,
    flowIndex,
    onChange
}, ref) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const viewRef = useRef<EditorView | null>(null);
    const isUpdatingRef = useRef(false);

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
        highlightErrorAt: (lineNumber: number) => {
            const view = viewRef.current;
            if (!view) return;
            try {
                view.dispatch({
                    effects: setErrorLineEffect.of(lineNumber),
                });
            } catch (error) {
                console.error('Error highlighting line:', error);
            }
        },
        clearErrorHighlight: () => {
            const view = viewRef.current;
            if (!view) return;
            view.dispatch({
                effects: setErrorLineEffect.of(null)
            });
        },
    }), []);

    useEffect(() => {
        if (!editorRef.current) return;

        const onChangeRef = onChange;
        const startState = EditorState.create({
            doc: editorValue,
            extensions: [
                lineNumbers(),
                foldGutter(),
                codeFolding(),
                highlightActiveLine(),
                highlightSelectionMatches(),
                json(),
                autocompletion(),
                errorLineField,
                errorLineTheme,
                EditorView.lineWrapping,
                EditorView.updateListener.of((update) => {
                    if (update.docChanged && !isUpdatingRef.current) {
                        const newValue = update.state.doc.toString();
                        onChangeRef(newValue);
                    }
                }),
                EditorView.theme({
                    '&': {
                        fontSize: '14px',
                        fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
                        minHeight: '420px',
                        height: '420px',
                    },
                    '.cm-content': {
                        padding: '12px',
                        minHeight: '420px',
                    },
                    '.cm-scroller': {
                        overflow: 'auto',
                    },
                    '.cm-focused': {
                        outline: 'none',
                    },
                }),
            ],
        });

        const view = new EditorView({
            state: startState,
            parent: editorRef.current,
        });

        viewRef.current = view;

        return () => {
            view.destroy();
            viewRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (viewRef.current && editorValue !== viewRef.current.state.doc.toString()) {
            isUpdatingRef.current = true;
            viewRef.current.dispatch({
                changes: {
                    from: 0,
                    to: viewRef.current.state.doc.length,
                    insert: editorValue,
                },
            });
            isUpdatingRef.current = false;
        }
    }, [editorValue]);

    // Clear error highlight when JSON becomes valid
    useEffect(() => {
        if (isValid && viewRef.current) {
            viewRef.current.dispatch({
                effects: setErrorLineEffect.of(null)
            });
        }
    }, [isValid]);

    return (
        <Card className="shadow-sm">
            <CardHeader className="space-y-1">
                <CardTitle className="text-base font-semibold">JSON editor</CardTitle>
                <p className="text-xs text-muted-foreground">
                    Manually edit JSON. Buttons inject valid starting shapes. Use format for prettified output.
                </p>
            </CardHeader>
            <CardContent className="space-y-3">
                <div className={`rounded-md border overflow-hidden ${isValid ? 'border-muted' : 'border-destructive/60'}`}>
                    <div ref={editorRef} />
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Flow index: {flowIndex || '—'}</span>
                    <span className={isValid ? 'text-emerald-500' : 'text-rose-500'}>{validationMessage}</span>
                </div>
            </CardContent>
        </Card>
    );
});

EditorPanel.displayName = 'EditorPanel';

export default EditorPanel;
